import {
  DAY_MS,
  HOUR_MS,
  MAX_RANGE_DAYS,
  TX_TYPES,
  type TxSearchResult,
  type TxSummary,
  type TxType,
} from '@apm/contracts';
import { fromLocalInput, toLocalInput } from '../time';

/**
 * 검색 폼 ↔ 조회 파라미터 (7회차 결정 4).
 *
 * 순수 함수만 둔다. 컴포넌트는 폼 상태를 들고 여기서 만든 쿼리스트링을 그대로 붙인다.
 * 기간 입력은 `datetime-local`이라 **로컬 시각**이고, 서버는 epoch ms를 받는다.
 * 그 변환은 `time.ts`가 한 곳에서 한다.
 *
 * 19회차에 프로세스·클라이언트 IP 조건과 "실시간 연결 포함"이 붙었다.
 * 앞의 둘은 서버가 이미 받는 조건이고(15·16회차), 실시간 연결 거르기는 **화면에서** 한다 —
 * 서버 검색에는 "sse만 빼기"가 없고 그것 하나 때문에 조회 API를 늘리지 않는다
 * (2026-09-07 결정 4는 기본값을 정한 것이지 API를 늘리라는 뜻이 아니다).
 */

/** 값은 계약에 있다. 화면 안 호출부를 위해 이름만 다시 내보낸다 */
export { HOUR_MS, MAX_RANGE_DAYS };

export interface SearchForm {
  /** `datetime-local` 값. `YYYY-MM-DDTHH:mm` */
  from: string;
  to: string;
  service: string;
  /** OTel `service.instance.id` 동등 비교 (16회차) */
  instance: string;
  txType: TxType | '';
  status: 'ok' | 'fail' | 'error' | '';
  entry: string;
  /**
   * 요청 경로를 부분일치가 아니라 동등 비교로 (23-A회차 리뷰 4).
   * 통계·추이는 진입점을 동등 비교로 세므로 거기서 넘어온 조건은 이쪽이어야 그림과 목록이 맞는다
   */
  entryExact: boolean;
  tenantId: string;
  /** 클라이언트 IP 동등 비교 (15회차) */
  clientAddress: string;
  /**
   * User-Agent 부분일치, 대소문자 무시 (23-A회차, UI 피드백 14).
   * 상세의 "같은 사용자의 앞뒤 요청"이 여기로 넘겨 온다. 서버는 15회차부터 이 조건을 받고 있었다
   */
  userAgent: string;
  /** 빈 문자열이면 조건 없음 */
  minDurationMs: string;
  /** 실시간 연결을 결과에 넣을지. 기본은 뺀다 (2026-09-07 결정 4) */
  includeSse: boolean;
}

/** `GET /api/tx/search` 응답. 봉투는 계약에 있다 (17회차) */
export type { TxSearchResult };

/** epoch ms ↔ `datetime-local`. `time.ts`의 것을 그대로 쓴다 */
export { fromLocalInput, toLocalInput };

/** 기본값: 최근 1시간 */
export function defaultForm(now: number = Date.now()): SearchForm {
  return {
    from: toLocalInput(now - HOUR_MS),
    to: toLocalInput(now),
    service: '',
    instance: '',
    txType: '',
    status: '',
    entry: '',
    entryExact: false,
    tenantId: '',
    clientAddress: '',
    userAgent: '',
    minDurationMs: '',
    includeSse: false,
  };
}

/**
 * 주소에 실린 **원래 기간**(epoch ms). 링크가 준 초·ms를 폼이 뭉개기 전에 꺼내 쓴다
 * (23-A회차 리뷰 6). 폼의 `datetime-local`은 ms를 담지 못하므로 첫 조회만 이 값을 쓴다
 */
export function exactRange(query: string): { from?: number; to?: number } {
  const params = new URLSearchParams(query);
  const read = (key: string): number | undefined => {
    const raw = params.get(key);
    if (raw === null || raw === '') return undefined;
    const value = Number(raw);
    return Number.isFinite(value) ? value : undefined;
  };
  return { from: read('from'), to: read('to') };
}

/**
 * 주소의 쿼리스트링 → 폼 (10회차 결정 5).
 *
 * 알림에서 "이 시간대 그 요청들"로 건너뛸 때, 그리고 검색 조건을 그대로 붙여 넣을 때 쓴다
 * (후보 #8). 서버가 준 것은 epoch ms이고 폼은 로컬 시각 문자열이라 여기서 바꾼다.
 * 모르는 키와 모양이 틀린 값은 무시한다.
 */
export function formFromParams(query: string, now: number = Date.now()): SearchForm {
  const params = new URLSearchParams(query);
  const form = defaultForm(now);
  const from = Number(params.get('from'));
  const to = Number(params.get('to'));
  if (Number.isFinite(from) && params.get('from') !== null) form.from = toLocalInput(from);
  if (Number.isFinite(to) && params.get('to') !== null) form.to = toLocalInput(to);
  // 전체 보기의 인스턴스가 어느 서비스 것인지. `service`는 27회차 전 옛 주소도 읽는다.
  form.service = params.get('instanceService') ?? params.get('service') ?? '';
  form.instance = params.get('instance') ?? '';
  // 동등 비교로 온 조건이 이기고, 그때는 '정확히 일치'가 켜진 채로 폼에 앉는다
  const exact = params.get('entryExact');
  form.entry = exact ?? params.get('entry') ?? '';
  form.entryExact = exact !== null && exact !== '';
  form.tenantId = params.get('tenantId') ?? '';
  form.clientAddress = params.get('clientAddress') ?? '';
  form.userAgent = params.get('userAgent') ?? '';

  const txType = params.get('txType');
  if (txType !== null && (TX_TYPES as readonly string[]).includes(txType)) {
    form.txType = txType as TxType;
  }
  const status = params.get('status');
  if (status === 'ok' || status === 'fail' || status === 'error') form.status = status;
  const minDuration = params.get('minDurationMs');
  if (minDuration !== null && Number.isFinite(Number(minDuration))) {
    form.minDurationMs = minDuration;
  }
  // 유형을 sse로 콕 집었으면 당연히 포함이다. 그 경우까지 걸러 내면 결과가 늘 비어 보인다
  form.includeSse = params.get('includeSse') === '1' || form.txType === 'sse';
  return form;
}

/** 보내기 전에 막을 것. 통과하면 undefined */
export function validateForm(form: SearchForm): string | undefined {
  const from = fromLocalInput(form.from);
  const to = fromLocalInput(form.to);
  if (from === undefined) return '시작 시각을 입력할 것';
  if (to === undefined) return '끝 시각을 입력할 것';
  if (to < from) return '끝 시각이 시작 시각보다 앞선다';
  if (to - from > MAX_RANGE_DAYS * DAY_MS) return `조회 기간은 최대 ${MAX_RANGE_DAYS}일이다`;
  if (form.minDurationMs !== '' && !Number.isFinite(Number(form.minDurationMs))) {
    return '최소 응답시간은 숫자여야 한다';
  }
  return undefined;
}

/** 폼 → 조회 파라미터. 빈 값은 `api.ts`의 `toQuery`가 걸러 낸다 */
export function toSearchParams(
  form: SearchForm,
  limit: number,
  cursor?: string,
): Record<string, string | number | undefined> {
  return {
    from: fromLocalInput(form.from),
    to: fromLocalInput(form.to),
    service: form.service.trim(),
    instance: form.instance.trim(),
    txType: form.txType,
    status: form.status,
    entry: form.entryExact ? undefined : form.entry.trim(),
    entryExact: form.entryExact ? form.entry.trim() : undefined,
    tenantId: form.tenantId.trim(),
    clientAddress: form.clientAddress.trim(),
    userAgent: form.userAgent.trim(),
    minDurationMs: form.minDurationMs === '' ? undefined : Number(form.minDurationMs),
    limit,
    cursor,
  };
}

/**
 * 폼 → 주소에 실을 쿼리스트링 (후보 #8).
 * 페이지 상한과 커서는 넣지 않는다. 주소는 "이 조건"이지 "이 페이지"가 아니다.
 */
export function toAddressQuery(form: SearchForm): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(toAddressUpdates(form))) {
    if (value === undefined) continue;
    params.set(key, value);
  }
  return params.toString();
}

/**
 * 폼 → 주소에 갈아 끼울 키 전부 (23-A회차 리뷰 3).
 *
 * 비운 조건은 값이 `undefined`다 — `mergeQuery`가 그 키를 **지운다**. 설정된 것만 돌려주면
 * 조건을 지웠을 때 옛 값이 주소에 남는다. 요청 화면은 분포도와 조건을 나눠 쓰므로
 * 주소를 통째로 덮을 수 없다.
 */
export function toAddressUpdates(form: SearchForm): Record<string, string | undefined> {
  const source = toSearchParams(form, 0);
  const updates: Record<string, string | undefined> = {};
  for (const [key, value] of Object.entries(source)) {
    if (key === 'limit' || key === 'cursor') continue;
    updates[key] = value === undefined || value === '' ? undefined : String(value);
  }
  updates.includeSse = form.includeSse ? '1' : undefined;
  return updates;
}

/** "더 보기"로 쌓는 검색 결과의 상한 (D-C3). 넘으면 먼저 받은 것부터 뺀다 */
export const SEARCH_ROWS_LIMIT = 5_000;

/**
 * 앞에 받은 행 뒤에 새로 받은 행을 붙이고, 상한을 넘으면 **먼저 받은 것부터** 뺀다 (D-C3).
 * 가상화 없이 표 하나에 행을 끝없이 올리면 더 보기를 누를수록 화면이 무거워진다.
 * `dropped`는 이번까지 뺀 누적 수다
 */
export function appendCapped(
  prev: readonly TxSummary[],
  next: readonly TxSummary[],
  dropped: number,
  limit: number = SEARCH_ROWS_LIMIT,
): { rows: TxSummary[]; dropped: number } {
  const rows = prev.concat(next);
  const over = rows.length - limit;
  if (over <= 0) return { rows, dropped };
  return { rows: rows.slice(over), dropped: dropped + over };
}

/**
 * 실시간 연결 거르기. 화면에서 한다(위 설명 참고).
 * 몇 건을 숨겼는지 함께 돌려준다. 숨긴 것을 말하지 않으면 총 건수와 표가 어긋나 보인다.
 */
export function applySseFilter(
  rows: readonly TxSummary[],
  includeSse: boolean,
): { rows: TxSummary[]; hidden: number } {
  if (includeSse) return { rows: [...rows], hidden: 0 };
  const kept = rows.filter((row) => row.txType !== 'sse');
  return { rows: kept, hidden: rows.length - kept.length };
}
